/**
 * NPC Spawn Behavior
 * 
 * Marks a location where an NPC should be spawned. Parameters specify:
 * - character_id: Which character definition to use
 * - spawn_radius: Optional random offset from exact position
 * - patrol_*: Patrol behavior parameters
 * - npc_*: AI behavior configuration
 * 
 * This is a marker behavior that triggers NPC spawning during level load.
 */

import { EntityType } from '../../types/EntityTypes';
import type { EntityBehavior, NodeProcessContext, NodeProcessResult } from '../../core/BehaviorRegistry';
import type { BehaviorParameterSchema } from '../../core/BehaviorSchema';

const parameters: BehaviorParameterSchema = {
  character_id: {
    type: 'enum',
    enumValues: ['lilly', 'daniel', 'hotdog', 'lavazombie'],
    required: true,
    default: 'hotdog',
    description: 'Character definition ID to spawn'
  },
  spawn_radius: {
    type: 'number',
    default: 0,
    min: 0,
    max: 10,
    description: 'Random spawn offset radius from marker position'
  },
  patrol_left_distance: {
    type: 'number',
    default: 0,
    min: 0,
    max: 50,
    description: 'Patrol distance to the left'
  },
  patrol_right_distance: {
    type: 'number',
    default: 0,
    min: 0,
    max: 50,
    description: 'Patrol distance to the right'
  },
  patrol_wait_time: {
    type: 'number',
    default: 1.0,
    min: 0,
    max: 10,
    description: 'Seconds to wait at patrol endpoints'
  },
  npc_behavior_id: {
    type: 'string',
    default: '',
    description: 'Custom AI behavior override ID'
  },
  npc_movement: {
    type: 'enum',
    enumValues: ['patrol', 'idle', 'follow', 'wander'],
    default: 'patrol',
    description: 'NPC movement pattern'
  },
  npc_combat: {
    type: 'enum',
    enumValues: ['passive', 'aggressive', 'defensive', 'territorial'],
    default: 'aggressive',
    description: 'NPC combat behavior'
  },
  npc_attack_range: {
    type: 'number',
    default: 2.0,
    min: 0.5,
    max: 20,
    description: 'Attack range in units'
  },
  npc_detection_range: {
    type: 'number',
    default: 10.0,
    min: 1,
    max: 50,
    description: 'Player detection range in units'
  },
  npc_attack_cooldown: {
    type: 'number',
    default: 1.5,
    min: 0.1,
    max: 10,
    description: 'Seconds between attacks'
  },
  max_health: {
    type: 'number',
    default: 0,
    min: 0,
    max: 1000,
    description: 'Max health override (0 = use character default)'
  },
  start_health: {
    type: 'number',
    default: 0,
    min: 0,
    max: 1000,
    description: 'Starting health override (0 = use max_health)'
  }
};

export const npcSpawnBehavior: EntityBehavior = {
  id: 'spawn:npc',
  displayName: 'NPC Spawn',
  description: 'Spawns an NPC at this location with configurable AI behavior.',
  type: EntityType.STATIC_GEOMETRY, // Marker, spawns CHARACTER separately
  category: 'spawn',
  tags: ['spawn', 'npc', 'marker'],
  parameters,
  
  // Process during level load: extract NPC spawn data
  onProcessNode: (ctx: NodeProcessContext): NodeProcessResult => {
    ctx.mesh.computeWorldMatrix(true);
    
    // Get parameters with defaults
    const characterId = ctx.getParam<string>('character_id', '');
    const spawnRadius = ctx.getParam<number>('spawn_radius', 0);
    const patrolLeftDistance = ctx.getParam<number>('patrol_left_distance', 0);
    const patrolRightDistance = ctx.getParam<number>('patrol_right_distance', 0);
    const patrolWaitTime = ctx.getParam<number>('patrol_wait_time', 1.0);
    const npcBehaviorId = ctx.getParam<string>('npc_behavior_id', '');
    const npcMovement = ctx.getParam<string>('npc_movement', 'patrol');
    const npcCombat = ctx.getParam<string>('npc_combat', 'aggressive');
    const attackRange = ctx.getParam<number>('npc_attack_range', 2.0);
    const detectionRange = ctx.getParam<number>('npc_detection_range', 10.0);
    const attackCooldown = ctx.getParam<number>('npc_attack_cooldown', 1.5);
    const maxHealth = ctx.getParam<number>('max_health', 0);
    const startHealth = ctx.getParam<number>('start_health', 0);
    
    if (!characterId) {
      throw new Error(
        `NPC marker '${ctx.mesh.name}' missing required property 'character_id'. ` +
        `Add hm_behavior_character_id property in Blender.`
      );
    }
    
    // Add to NPC spawns collection
    // Use full mesh name to preserve unique Blender suffixes (.001, .002, etc.)
    ctx.results.npcSpawns.push({
      id: ctx.mesh.name,
      characterId,
      position: ctx.mesh.getAbsolutePosition().clone(),
      spawnRadius,
      patrolLeftDistance,
      patrolRightDistance,
      patrolWaitTime,
      behaviorConfig: {
        behaviorId: npcBehaviorId || undefined,
        movement: npcMovement as 'patrol' | 'idle' | 'follow' | 'wander',
        combat: npcCombat as 'passive' | 'aggressive' | 'defensive' | 'territorial',
        attackRange: attackRange || undefined,
        detectionRange: detectionRange || undefined,
        attackCooldown: attackCooldown || undefined,
        patrolLeft: patrolLeftDistance,
        patrolRight: patrolRightDistance,
      },
      maxHealth: maxHealth > 0 ? maxHealth : undefined,
      startHealth: startHealth > 0 ? startHealth : undefined,
    });
    
    // Hide the marker mesh
    ctx.mesh.isVisible = false;
    
    return { 
      handled: true, 
      skipPhysics: true, 
      hideNode: true 
    };
  },
  
  // No state machine - NPC spawning handled by LevelBuilder
  createMachine: undefined,
  
  onSpawn: undefined,
};
